import type { ModalCloseReason } from "@/components/modal/modalCloseReason";
import { captureEvent } from "@/lib/analytics";
export type GoodAddingWizardStep = "editGoodInfoManually" | "editGoodWithAI" | "viewGoodVersion" | "viewGoodVersionsList";

export type ImprovePhase = "pipeline" | "editor";

export type ContentCreationOpenTrigger =
  | "marketplace_modal"
  | "onboarding"
  | "goods_card"
  | "goods_list"
  | "recommendations"
  | "session_restore"
  | "unknown";

export type ContentCreationAbandonReason = ModalCloseReason | "header_back" | "navigation_away" | "programmatic" | "unknown";

export type ContentCreationWizardPhase = 1 | 2;

type SessionContext = {
  marketplace: string | null;
  content_type: string | null;
  product_source_type: string | null;
  open_trigger: ContentCreationOpenTrigger;
};

type ActiveSession = {
  id: string;
  openedAt: number;
  stepEnteredAt: number;
  openIndex: number;
  phase: ContentCreationWizardPhase;
  wizardStep: GoodAddingWizardStep;
  reachedStep2: boolean;
  completed: boolean;
  context: SessionContext;
  improvePhase: ImprovePhase | null;
};

let activeSession: ActiveSession | null = null;
let browserSessionOpenCount = 0;
let recentAbandonAt: number[] = [];
let pendingOpenTrigger: ContentCreationOpenTrigger = "unknown";

const QUICK_DISMISS_MS = 3000;
const HESITATION_MS = 15000;
const RAGE_WINDOW_MS = 5 * 60 * 1000;
const REPEAT_ABANDON_WINDOW_MS = 2 * 60 * 1000;

function createSessionId(): string {
  return `cc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function wizardPhaseFromStep(step: GoodAddingWizardStep): ContentCreationWizardPhase | null {
  if (step === "editGoodInfoManually") return 1;
  if (step === "editGoodWithAI") return 2;
  return null;
}

function pruneAbandonTimestamps(now = Date.now()): void {
  recentAbandonAt = recentAbandonAt.filter((t) => now - t < RAGE_WINDOW_MS);
}

function countRecentAbandons(windowMs: number, now = Date.now()): number {
  return recentAbandonAt.filter((t) => now - t < windowMs).length;
}

function buildRageSignals(session: ActiveSession, timeOpenMs: number, now = Date.now()) {
  const priorAbandons5m = countRecentAbandons(RAGE_WINDOW_MS, now);
  const priorAbandons2m = countRecentAbandons(REPEAT_ABANDON_WINDOW_MS, now);
  const isQuickDismiss = timeOpenMs < QUICK_DISMISS_MS;
  const isRepeatAbandon = session.openIndex >= 2;
  const hadProgress =
    session.reachedStep2 ||
    Boolean(session.context.content_type) ||
    Boolean(session.context.product_source_type);

  const isRageQuit =
    (isQuickDismiss && isRepeatAbandon) ||
    priorAbandons2m >= 2 ||
    (isQuickDismiss && priorAbandons5m >= 1);

  return {
    is_quick_dismiss: isQuickDismiss,
    is_repeat_abandon: isRepeatAbandon,
    is_rage_quit: isRageQuit,
    hesitation_without_progress: timeOpenMs >= HESITATION_MS && !hadProgress,
    had_progress: hadProgress,
    prior_abandon_count_5m: priorAbandons5m,
    prior_abandon_count_2m: priorAbandons2m,
  };
}

function sessionPayload(session: ActiveSession) {
  return {
    wizard_session_id: session.id,
    wizard_phase: session.phase,
    wizard_step: session.wizardStep,
    reached_step_2: session.reachedStep2,
    open_trigger: session.context.open_trigger,
    marketplace: session.context.marketplace,
    content_type: session.context.content_type,
    product_source_type: session.context.product_source_type,
    improve_phase: session.improvePhase,
    open_index: session.openIndex,
  };
}

/** Вызвать перед `open = true`, чтобы зафиксировать источник открытия. */
export function markContentCreationOpenTrigger(trigger: ContentCreationOpenTrigger): void {
  pendingOpenTrigger = trigger;
}

export function syncContentCreationWizardContext(ctx: Partial<SessionContext>): void {
  const session = activeSession;
  if (!session || session.completed) return;
  if (ctx.marketplace !== undefined) session.context.marketplace = ctx.marketplace;
  if (ctx.content_type !== undefined) session.context.content_type = ctx.content_type;
  if (ctx.product_source_type !== undefined) session.context.product_source_type = ctx.product_source_type;
}

export function contentCreationWizardOpened(args: {
  wizardStep: GoodAddingWizardStep;
  marketplace?: string | null;
  content_type?: string | null;
  product_source_type?: string | null;
}): void {
  browserSessionOpenCount += 1;
  const openIndex = browserSessionOpenCount;
  pruneAbandonTimestamps();

  const phase = wizardPhaseFromStep(args.wizardStep) ?? 1;
  const trigger = pendingOpenTrigger;
  pendingOpenTrigger = "unknown";

  activeSession = {
    id: createSessionId(),
    openedAt: performance.now(),
    stepEnteredAt: performance.now(),
    openIndex,
    phase,
    wizardStep: args.wizardStep,
    reachedStep2: phase === 2,
    completed: false,
    improvePhase: null,
    context: {
      open_trigger: trigger,
      marketplace: args.marketplace ?? null,
      content_type: args.content_type ?? null,
      product_source_type: args.product_source_type ?? null,
    },
  };

  captureEvent("[CardGenius] Открытие создания контента", {
    ...sessionPayload(activeSession),
    prior_abandon_count_5m: countRecentAbandons(RAGE_WINDOW_MS),
  });
}

export function contentCreationWizardStepChanged(args: {
  wizardStep: GoodAddingWizardStep;
  improvePhase?: ImprovePhase | null;
}): void {
  const session = activeSession;
  if (!session || session.completed) return;

  const nextPhase = wizardPhaseFromStep(args.wizardStep);
  if (nextPhase == null) return;

  const now = performance.now();
  const timeOnPreviousStepMs = Math.round(now - session.stepEnteredAt);

  session.wizardStep = args.wizardStep;
  session.phase = nextPhase;
  session.stepEnteredAt = now;
  session.improvePhase = args.improvePhase ?? (nextPhase === 2 ? session.improvePhase : null);

  if (nextPhase === 2) {
    session.reachedStep2 = true;
  }

  syncContentCreationWizardContext({});
}

export function contentCreationWizardAdvancedToStep2(extra?: Record<string, unknown>): void {
  const session = activeSession;
  if (!session || session.completed) return;

  const timeOnStep1Ms = Math.round(performance.now() - session.stepEnteredAt);

  session.reachedStep2 = true;
  session.phase = 2;
  session.wizardStep = "editGoodWithAI";
  session.stepEnteredAt = performance.now();

  captureEvent("[CardGenius] Переход на шаг 2 создания контента", {
    ...sessionPayload(session),
    time_on_step_1_ms: timeOnStep1Ms,
    ...extra,
  });
}

export function contentCreationWizardReturnedToStep1(): void {
  const session = activeSession;
  if (!session || session.completed) return;

  const timeOnStep2Ms = Math.round(performance.now() - session.stepEnteredAt);

  session.phase = 1;
  session.wizardStep = "editGoodInfoManually";
  session.stepEnteredAt = performance.now();
  session.improvePhase = null;

  captureEvent("[CardGenius] Возврат на шаг 1 создания контента", {
    ...sessionPayload(session),
    time_on_step_2_ms: timeOnStep2Ms,
  });
}

export function contentCreationWizardCompleted(args: {
  completion_type: "saved_draft" | "published" | "view_versions";
}): void {
  const session = activeSession;
  if (!session) return;

  session.completed = true;
  const timeOpenMs = Math.round(performance.now() - session.openedAt);
  const timeOnCurrentStepMs = Math.round(performance.now() - session.stepEnteredAt);

  captureEvent("[CardGenius] Успешное завершение создания контента", {
    ...sessionPayload(session),
    completion_type: args.completion_type,
    time_open_ms: timeOpenMs,
    time_on_current_step_ms: timeOnCurrentStepMs,
  });

  activeSession = null;
}

export function contentCreationWizardAbandoned(reason: ContentCreationAbandonReason): void {
  const session = activeSession;
  if (!session || session.completed) return;

  const phase = wizardPhaseFromStep(session.wizardStep);
  if (phase == null) {
    activeSession = null;
    return;
  }

  const timeOpenMs = Math.round(performance.now() - session.openedAt);
  const timeOnCurrentStepMs = Math.round(performance.now() - session.stepEnteredAt);
  const rage = buildRageSignals(session, timeOpenMs);

  recentAbandonAt.push(Date.now());
  pruneAbandonTimestamps();

  captureEvent("[CardGenius] Закрытие создания контента", {
    ...sessionPayload(session),
    close_reason: reason,
    abandon_phase: phase,
    time_open_ms: timeOpenMs,
    time_on_current_step_ms: timeOnCurrentStepMs,
    is_mobile_viewport: typeof window !== "undefined" && window.innerWidth < 640,
    ...rage,
  });

  activeSession = null;
}

export function contentCreationWizardAbandonOnNavigationAway(): void {
  if (!activeSession || activeSession.completed) return;
  const phase = wizardPhaseFromStep(activeSession.wizardStep);
  if (phase == null) return;
  contentCreationWizardAbandoned("navigation_away");
}

export function isContentCreationWizardAnalyticsActive(): boolean {
  if (!activeSession || activeSession.completed) return false;
  return wizardPhaseFromStep(activeSession.wizardStep) != null;
}

export function updateContentCreationImprovePhase(phase: ImprovePhase): void {
  const session = activeSession;
  if (!session || session.completed) return;
  session.improvePhase = phase;
}

/** Контекст активной сессии мастера для вложенных модалок (редактор фото и т.п.). */
export function getContentCreationWizardAnalyticsContext(): {
  wizard_session_id: string;
  wizard_phase: ContentCreationWizardPhase;
  wizard_step: GoodAddingWizardStep;
  open_trigger: ContentCreationOpenTrigger;
  marketplace: string | null;
  content_type: string | null;
  improve_phase: ImprovePhase | null;
} | null {
  const session = activeSession;
  if (!session || session.completed) return null;
  const phase = wizardPhaseFromStep(session.wizardStep);
  if (phase == null) return null;
  return {
    wizard_session_id: session.id,
    wizard_phase: phase,
    wizard_step: session.wizardStep,
    open_trigger: session.context.open_trigger,
    marketplace: session.context.marketplace,
    content_type: session.context.content_type,
    improve_phase: session.improvePhase,
  };
}
